import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getReportSteps,
  bulkCreateReportSteps,
  bulkUpdateReportSteps,
  deleteReportStep,
  type ReportStep,
  type ReportStepPhase,
} from '../../services';

export const reportStepKeys = {
  all: ['reportSteps'] as const,
  lists: () => [...reportStepKeys.all, 'list'] as const,
  /**
   * `phase` is part of the key (ADR 143). The two fetches hit the same URL
   * path for the same alert type and differ only by query parameter, so
   * without it the intake and end-event-survey lists share one cache entry
   * and whichever resolves last overwrites the other.
   */
  list: (alertTypeId: number, phase: ReportStepPhase) =>
    [...reportStepKeys.lists(), alertTypeId, phase] as const,
};

export function useReportSteps(
  alertTypeId: number,
  phase: ReportStepPhase = 'intake',
) {
  return useQuery({
    queryKey: reportStepKeys.list(alertTypeId, phase),
    queryFn: () => getReportSteps(alertTypeId, phase),
    enabled: !!alertTypeId,
  });
}

export function useBulkCreateReportSteps() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkCreateReportSteps,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportStepKeys.lists() });
    },
  });
}

export function useBulkUpdateReportSteps() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkUpdateReportSteps,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportStepKeys.lists() });
    },
  });
}

export function useDeleteReportStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteReportStep,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportStepKeys.lists() });
    },
  });
}
